import { useMemo, useState } from "react";
import { CheckCircle2, Trash2 } from "lucide-react";
import { AcDialog, Button, Icon, Modal, SplitButton, DialogCancel } from "@cntrl/ui";
import { useShow, useDeleteShow, useConfirmHold } from "../../app/hooks/useShows.js";
import type { SharedShowDetail } from "../../app/hooks/useShowShares.js";
import { errorMessage } from "../../app/hooks/apiError.js";
import { ShowWizard } from "./ShowWizard.js";
import { sharedShowFormatDate } from "./sharedShowFormat.js";
import type { ShowCreateSeed } from "./showCreateSeed.js";
import { EditorFooterProvider } from "./EditorFooterSlot.js";
import { EditorSaveProvider, useSaveRegistry, usePendingInviteCount } from "./EditorSaveRegistry.js";
import { longDate, tMinus, todayIso } from "./lib/bookingRows.js";
import styles from "./ShowEditorModal.module.scss";

export interface ShowEditorModalProps {
  /** Undefined = create (`/booking/shows/new`); set = edit. Both render THIS shell — only the mode line flips. */
  showId?: string;
  /**
   * Starting values for a CREATE — see {@link ShowCreateSeed}. The booking lens passes one when converting a
   * pre-hold offer into a hold; every other caller passes nothing and the editor opens blank as it always has.
   * Forwarded straight to the wizard, which is where the sections that can use it live.
   */
  seed?: ShowCreateSeed;
  /** The active section (1-10) from `?step`; refresh/back land here. */
  step: number;
  onStep: (step: number) => void;
  onCreated: (showId: string) => void;
  /**
   * A create side effect that must fire whether the editor STAYS OPEN or DISMISSES — the booking lens uses it
   * to convert the pre-hold offer this show was created from (ART-139). `onCreated` cannot carry it: on a
   * clean create the editor dismisses via `onSaved` and never calls `onCreated`, so an offer converted only
   * there was left `open` (a duplicate card on the board). Called once, before the stay/dismiss decision.
   * AWAITED: it may return a promise (the convert POST), and the save waits for it before it dismisses and
   * the host refetches — otherwise the board refreshes before the offer is stamped `converted` and the offer
   * card is still on it ("refreshed before the data landed", ART-139).
   */
  onShowCreated?: (showId: string) => void | Promise<void>;
  onExit: () => void;
  onDeleted: () => void;
  /**
   * A fully-clean save closes the editor and returns to the list (Ryan, 2026-08-23). Distinct from `onExit`:
   * that is a dismiss (Cancel / Escape / scrim), this is a successful commit.
   *
   * `confirmed` is true when the hold was ALSO promoted, so the caller can land on the tab the show now
   * lives on — a confirmed show has left Holds, and staying there shows the tab it just left.
   */
  onSaved: (result: { confirmed: boolean }) => void;
  /**
   * GUEST MODE (ART-165 shared shell). When set, an accepted guest is editing a shared show: the shell reads
   * the show from this matrix (not the owner-fenced `useShow`), the header shows the inviter's basics
   * read-only, and the footer drops Delete + Confirm (a guest owns neither) — only Save remains, fanning out
   * to the guest's own editable sections. `showId` is the inviter's show id.
   */
  guest?: SharedShowDetail;
}

/**
 * The show editor — **one popup for both Add and Edit**, over a still-mounted Booking list.
 *
 * This is the half of the regression Ryan named ("completely missing the pop up window for add show which is
 * the same for edit a show"). Add and Edit are the same component with the same chrome; only the mode line
 * changes, and creating a show swaps the URL underneath a modal that never closes, so the two are visibly one
 * window rather than two screens.
 *
 * It stays URL-addressable (`/booking/shows/new`, `/booking/shows/{id}/edit`) as a routed child of `/booking`
 * — this codebase's routed-modal convention, so refresh / back / deep-link keep working. The reference's "no
 * URL change" is about not NAVIGATING AWAY, which the routed child satisfies: the list stays mounted behind
 * the scrim.
 *
 * The overlay chrome — scrim, focus trap, the Escape stack, the body-scroll lock — belongs to `Modal`; the
 * header type is the primitive's too. It opts into `titleVariant="headline"`, because this dialog's title is
 * the VENUE — the subject's own name, not a label naming the dialog — and ruling 3's 16px tracked all-caps
 * lockup reads as chrome on a name (reported as such, ART-88). `headline` is ruling 6's page-H1 treatment,
 * Archivo Black sentence case, which is also what the reference ships for this header.
 */
export function ShowEditorModal({ showId, seed, step, onStep, onCreated, onShowCreated, onExit, onDeleted, onSaved, guest }: ShowEditorModalProps) {
  // Guest mode reads the show from the matrix (`guest`), never the owner-fenced `useShow` (disabled here).
  const show = useShow(guest ? undefined : showId);
  const detail = show.data?.data;
  // The guest editor is reached ONLY through the view modal's Edit button, which is itself gated on
  // `schedule:write` (ART-165 point 1a). So a read-only guest member never lands here — they read the show in
  // the view modal — and there is no "fully view-only editor" mode any more: the editor always offers Save,
  // and the guest's own sections are always editable (the server is the tamper-proof floor regardless).
  const deleteShow = useDeleteShow();
  const confirmHold = useConfirmHold();
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  // The footer's two portal hosts. Callback refs into state (not a ref) so the first paint after mount
  // re-renders the sections with a live host to portal their Save into.
  const [saveHost, setSaveHost] = useState<HTMLElement | null>(null);
  const [extraHost, setExtraHost] = useState<HTMLElement | null>(null);
  const footerHosts = useMemo(
    () => ({ save: saveHost, extra: extraHost }),
    [saveHost, extraHost],
  );

  const isHold = !!showId && detail?.status !== "confirmed" && detail?.status !== "settled";
  /*
   * The meta line's leading segment. It used to be the MODE — `New hold` / `Editing hold` / `Editing show`,
   * relocated out of the deleted header eyebrow (ruling 10). ART-88 asked for "Editing show" to go, and the
   * VERB is what was wrong with it: it narrates a mode the reader can already see — they are in the editor,
   * the venue is the title, the ribbon says Save and Delete. So the verb goes in all three modes.
   *
   * What is NOT redundant is the STATUS, which this header carries nowhere else: a date that is still a HOLD
   * is not yet a show, and only the footer's "Confirm hold and save" (inside a menu) otherwise says so. So the
   * status noun survives on an existing hold and is EMPTY on a confirmed or settled show.
   *
   * `New hold` went on 2026-09-07 (PO, ART-89: *"the 'new hold' subheader can go away"*), reversing the note
   * that used to stand here — that dropping the word everywhere would leave a band creating a date untold it
   * starts as a hold. On a NEW show that line is the only thing under the title, and it is the one mode with
   * no date, city or countdown to sit beside: a whole subheader carrying one word about a record that does
   * not exist yet. On an existing hold the word rides a line the reader is already reading, which is why
   * that half stays.
   *
   * The empty string is filtered before the join, so a confirmed show — and now a new one — opens without an
   * orphaned leading separator.
   */
  const status = !guest && showId && isHold ? "Hold" : "";

  // `Hold · Friday, July 3, 2026 · Nashville, TN · T-9 days` — the header meta line; a confirmed show
  // drops the leading word and reads `Friday, July 3, 2026 · Nashville, TN · T-9 days`. In guest mode it comes
  // from the matrix (the inviter's basics, read-only).
  const meta = guest
    ? [sharedShowFormatDate(guest.date), [guest.city, guest.state].filter(Boolean).join(", ")].filter(Boolean).join(" · ")
    : [
        status,
        ...(detail
          ? [longDate(detail.date ?? ""), [detail.city, detail.state].filter(Boolean).join(", "), tMinus(detail.date ?? "", todayIso())]
          : []),
      ]
        .filter((part) => part !== "")
        .join(" · ");

  // A footer action that fails leaves the modal open with the reason visible — never a silent close.
  const footerError = deleteShow.isError
    ? errorMessage(deleteShow.error, "Couldn't delete this show, please refresh and try again.")
    : confirmHold.isError
      ? errorMessage(confirmHold.error, "Couldn't confirm this hold.")
      : "";

  const busy = deleteShow.isPending || confirmHold.isPending;

  return (
    <Modal
      size="3xl"
      // The card's ground, and with it the pane and the rail's open row — one value, so header, workbench
      // and ribbon are one continuous sheet (PO, ART-89 2026-09-07: `#f5f5f4`, revising the `#efefee` of the
      // round before). Named through the token that holds that value rather than written as a hex.
      className={styles.card}
      // `bodyLayout="flush"`: the editor's body is a two-pane WORKBENCH above a pinned ribbon, not a stack of
      // fields. It needs the card's own height to size against — `.wizard` fills it and the section pane
      // scrolls INSIDE it, while the ribbon stays put — and it draws its own gutter, which the primitive's
      // padded body would have doubled. Under the padded body (2026-09-01) the whole editor grew instead:
      // the rail scrolled away with the pane and the ribbon fell below the fold.
      title={(guest ? guest.venueName : detail?.venueName) || "Untitled show"}
      // The title here is the VENUE'S OWN NAME, not the dialog's name, so it takes the headline treatment
      // rather than ruling 3's chrome lockup — see `ModalTitleVariant`. It carries the meta line's ink with it.
      titleVariant="headline"
      subtitle={meta}
      closeLabel="Close editor"
      onClose={onExit}
      bodyLayout="flush"
    >
      {/* The footer ribbon is PINNED chrome, so the active section's primary Save portals INTO it (and the
          Financials section's `Save agent / manager % as defaults` link into the leading slot). The ribbon
          therefore reads `[★ defaults] … Delete · Confirm hold · Save`, as the reference does, and a Save is
          visible at rest on a short viewport instead of living below the fold of the scrolling panel. */}
      <EditorFooterProvider hosts={footerHosts}>
        <EditorSaveProvider>
        <ShowWizard
          showId={showId}
          seed={seed}
          step={step}
          onStep={onStep}
          onCreated={onCreated}
          onExit={onExit}
          guest={guest}
        />

        <footer className={styles.footer}>
          {footerError && (
            <p className={styles.error} role="alert">
              {footerError}
            </p>
          )}
          <div className={styles.extras} ref={setExtraHost} />
          <div className={styles.actions}>
            {/* A guest owns neither Delete nor Confirm — the show is the inviter's. Only Save (its own
                sections) remains. */}
            {!guest && showId && (
              <Button variant="ghost" size="sm" disabled={busy} onClick={() => setConfirmingDelete(true)}>
                <Icon icon={Trash2} size={16} /> Delete
              </Button>
            )}
            {/* Cancel lives here rather than inside Basics: it dismisses the EDITOR, so it belongs to the
                editor's chrome and stays reachable from anywhere in the scroll. In guest mode it returns to
                the read-only view modal. */}
            <DialogCancel size="sm" disabled={busy} />
            <div className={styles.saveSlot} ref={setSaveHost}>
              <FooterSave
                showId={showId}
                isHold={!guest && (!showId || isHold)}
                onCreated={onCreated}
                onShowCreated={onShowCreated}
                onConfirm={(id) => confirmHold.mutateAsync(id)}
                onSaved={onSaved}
              />
            </div>
          </div>
        </footer>
        </EditorSaveProvider>
      </EditorFooterProvider>

      {confirmingDelete && showId && (
        <AcDialog
          tone="danger"
          // ART-326: Delete is the one cancel — everyone attached is told, and it comes off every surface.
          title="Cancel this show?"
          message={`Cancel ${detail?.venueName ?? "this show"}${detail?.date ? ` · ${longDate(detail.date)}` : ""}? Everyone on this show will be told.`}
          confirmLabel="Cancel show"
          cancelLabel="Keep show"
          confirmIcon={Trash2}
          loading={deleteShow.isPending}
          onCancel={() => setConfirmingDelete(false)}
          onConfirm={() =>
            // onSuccess, never onSettled: a failed delete keeps the editor open and surfaces the reason.
            deleteShow.mutate(showId, {
              onSuccess: () => {
                setConfirmingDelete(false);
                onDeleted();
              },
              onError: () => setConfirmingDelete(false),
            })
          }
        />
      )}
    </Modal>
  );
}

/**
 * The ribbon's ONE Save — it commits every dirty section, creating the show first when there isn't one yet.
 *
 * This is what lets the editor open on a show that does not exist: sections hold their edits locally, and
 * the fan-out creates the record then flushes them in order. A fan-out is not a transaction, so a partial
 * failure is reported as one — the sections that did not save are named and the modal stays open, rather
 * than reporting success because most of it worked.
 */
function FooterSave({
  showId,
  isHold,
  onCreated,
  onShowCreated,
  onConfirm,
  onSaved,
}: {
  showId?: string;
  /** Is there a hold to promote? A brand-new show is created as one, so the option is offered there too. */
  isHold: boolean;
  onCreated: (showId: string) => void;
  /** Fired ONCE when a create lands, clean or partial — the create side effect (e.g. converting the offer
   *  this show came from), separate from the stay-open URL swap `onCreated` does. */
  onShowCreated?: (showId: string) => void | Promise<void>;
  onConfirm: (showId: string) => Promise<unknown>;
  /** Dismiss the editor after a fully-clean save. Never called on a partial failure. */
  onSaved: (result: { confirmed: boolean }) => void;
}) {
  const registry = useSaveRegistry();
  const pendingInvites = usePendingInviteCount();
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState<string[]>([]);
  if (!registry) return null;

  /**
   * Commit everything dirty, then optionally promote the hold.
   *
   * Confirming ALWAYS saves first, which is what the reference does — its "Confirm hold" is literally the
   * save path with a flag (`doSave(true)`), not a separate status toggle. Ours was status-only, so with all
   * ten sections editable in one scroll you could fill in half the show, press Confirm hold, and watch it go
   * confirmed while none of the edits landed. A partial save therefore ABORTS the confirm: promoting a hold
   * whose details did not save is the worst of both outcomes.
   */
  async function run(confirm: boolean) {
    setBusy(true);
    setFailed([]);
    try {
      const { showId: id, failures } = await registry!.saveAll();
      setFailed(failures);
      // A create is a create whether or not the fan-out fully landed, and whether it stays open or dismisses.
      // Fire the create side effect ONCE here — before the stay-open/dismiss branch below — so a caller that
      // must act on the new show (converting the offer it came from, ART-139) is not tied to `onCreated`,
      // which the clean-create path deliberately does NOT call (it dismisses via `onSaved` instead).
      // Awaited: the offer conversion (the convert POST) must LAND before the dismiss below fires the host's
      // refetch, or the board refreshes with the offer still `open` and its card is still on it (ART-139).
      if (id && !showId) await onShowCreated?.(id);
      // The URL swap belongs to the editor that STAYS OPEN. A create whose fan-out only partly landed — or
      // whose confirm then failed — is now editing a show that EXISTS, and a retry from a `/new` URL would
      // create a SECOND one. A clean create dismisses instead (below), so calling it there too would be a
      // second navigation racing the one the ruling asks for, with the outcome decided by whichever the
      // router happened to apply last (PO, 2026-09-11: a clean create dismisses to the list).
      const swapUrlUnderTheModal = () => {
        if (id && !showId) onCreated(id);
      };
      if (!id || failures.length > 0) {
        swapUrlUnderTheModal();
        return;
      }
      if (confirm) {
        try {
          await onConfirm(id);
        } catch {
          setFailed(["Confirm hold"]);
          swapUrlUnderTheModal();
          return;   // a failed confirm keeps the editor open with the reason visible
        }
      }
      // A clean save DISMISSES the editor (Ryan, 2026-08-23), and since 2026-09-11 that governs the CREATE
      // press too. The fan-out is not a transaction, so reaching here at all is conditional on a total
      // success: any failed section returned above and keeps the modal open with the sections named, which
      // is the "no phantom success" rule — closing on a partial write would report success by disappearing.
      // `id` is required too: with nothing created there is nothing to have saved.
      //
      // `confirm` is passed through so the caller can land on Confirmed: reaching here with it set means the
      // promote succeeded (a failed one returned above).
      onSaved({ confirmed: confirm });
    } finally {
      setBusy(false);
    }
  }

  // Saving SENDS something — the members section's staged crew get a push — so the button says so rather
  // than letting a notification leave under a word as quiet as "Save" (PO, 2026-08-29). The count is the
  // section's own, published through the registry; it is 0 whenever there is nobody new to reach, which is
  // every save that only moved the date.
  const invites = pendingInvites;
  const primaryLabel = showId
    ? invites > 0 ? `Save and invite ${invites}` : "Save"
    : invites > 0 ? `Create show and invite ${invites}` : "Create show";
  const confirmLabel = showId ? "Confirm hold and save" : "Confirm hold and create show";

  return (
    <>
      {failed.length > 0 && (
        <span className={styles.error} role="alert">
          {failed.join(", ")} didn’t save.
        </span>
      )}
      {/* The menu opens UPWARD: the ribbon is pinned to the bottom of the dialog, so a downward one would
          fall off the card. With no hold to promote `SplitButton` degrades to a plain button. */}
      <SplitButton
        placement="above"
        size="sm"
        loading={busy}
        menuLabel="More save options"
        onClick={() => void run(false)}
        actions={isHold ? [{ label: confirmLabel, icon: CheckCircle2, onSelect: () => void run(true) }] : []}
      >
        {primaryLabel}
      </SplitButton>
    </>
  );
}
